import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../db';
import { toast, confirmDialog } from '../utils/feedback';
import { MessageSquare, Heart, Image as ImageIcon, Send, X, MoreVertical, Trash2, Smile, CornerDownRight, ClipboardList, PlaySquare, Eye, Plus } from 'lucide-react';
import UserAvatar from '../components/UserAvatar';
import { ExercisePreviewModal } from '../components/ExerciseSelectorModal';

const Forum = ({ currentProfile }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [newPostText, setNewPostText] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [showImageOptions, setShowImageOptions] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  
  const [selectedRoutine, setSelectedRoutine] = useState(location.state?.sharedRoutine || null);
  const [showRoutineSelector, setShowRoutineSelector] = useState(false);
  const [userRoutines, setUserRoutines] = useState([]);
  const [allExercises, setAllExercises] = useState([]);
  const [previewingRoutine, setPreviewingRoutine] = useState(null);
  const [previewingExercise, setPreviewingExercise] = useState(null);
  
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const EMOJIS = ['😀', '😂', '😊', '😍', '🤔', '😎', '😭', '😡', '👍', '👎', '👏', '🙌', '💪', '🔥', '❤️', '🎉', '✨', '💯', '👀', '😅', '🥺', '😏', '🤷‍♂️', '🤦‍♂️'];
  
  const [activeCommentPost, setActiveCommentPost] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  
  const [allProfiles, setAllProfiles] = useState([]);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [filteredProfiles, setFilteredProfiles] = useState([]);

  const [commentMentionQuery, setCommentMentionQuery] = useState(null);
  const [filteredCommentProfiles, setFilteredCommentProfiles] = useState([]);
  const commentInputRef = useRef(null);
  const [expandedRoutines, setExpandedRoutines] = useState({});

  const [showGifSelector, setShowGifSelector] = useState(false);
  const [gifs, setGifs] = useState([]);
  const [gifSearch, setGifSearch] = useState('');
  const [isSearchingGifs, setIsSearchingGifs] = useState(false);
  const [gifNextPos, setGifNextPos] = useState('');
  const [isLoadingMoreGifs, setIsLoadingMoreGifs] = useState(false);

  const fetchGifs = async (query = '', isLoadMore = false) => {
    const apiKey = import.meta.env.VITE_GIPHY_API_KEY;
    if (!apiKey) {
      setIsSearchingGifs(false);
      return;
    }

    if (isLoadMore) {
      setIsLoadingMoreGifs(true);
    } else {
      setIsSearchingGifs(true);
    }
    try {
      const offset = isLoadMore ? parseInt(gifNextPos || '0', 10) : 0;
      let url = query.trim() 
        ? `https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(query)}&limit=20&offset=${offset}`
        : `https://api.giphy.com/v1/gifs/trending?api_key=${apiKey}&limit=20&offset=${offset}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.meta && data.meta.status !== 200) {
        toast.error("Erreur API Giphy: " + data.meta.msg);
        return;
      }
      
      if (isLoadMore) {
        setGifs(prev => [...prev, ...(data.data || [])]);
      } else {
        setGifs(data.data || []);
      }
      setGifNextPos((offset + 20).toString());
    } catch (e) {
      console.error("Error fetching GIFs:", e);
    }
    setIsSearchingGifs(false);
    setIsLoadingMoreGifs(false);
  };

  const handleGifScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !isLoadingMoreGifs && !isSearchingGifs && gifNextPos) {
      fetchGifs(gifSearch, true);
    }
  };
  
  useEffect(() => {
    if (showGifSelector && gifs.length === 0) {
      fetchGifs();
    }
  }, [showGifSelector]);

  useEffect(() => {
    const unsubscribe = db.listenToPosts((data) => {
      setPosts(data);
      setLoading(false);
    });
    db.getProfiles().then(setAllProfiles);
    db.getExercises(currentProfile?.id).then(setAllExercises);
    return () => unsubscribe();
  }, [currentProfile?.id]);

  const resolveExerciseDef = (item) => {
    if (!item) return null;
    const match = allExercises?.find(e => 
      (item.exerciseId && e.id === item.exerciseId) || 
      (item.id && e.id === item.id) || 
      (e.name && item.name && e.name.toLowerCase().trim() === item.name.toLowerCase().trim())
    );
    return match ? { ...item, ...match } : item;
  };

  useEffect(() => {
    if (!loading && location.hash) {
      const hashParts = location.hash.substring(1).split('_');
      const postId = hashParts[0];
      const commentId = hashParts[1];

      const element = document.getElementById(`post-${postId}`);
      if (element) {
        setTimeout(() => {
          if (commentId) setActiveCommentPost(postId);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.style.transition = 'box-shadow 0.5s, transform 0.3s';
          element.style.boxShadow = '0 0 15px var(--primary-color)';
          element.style.transform = 'scale(1.02)';
          setTimeout(() => {
            element.style.boxShadow = '';
            element.style.transform = 'scale(1)';
            
            if (commentId) {
              setTimeout(() => {
                const commentEl = document.getElementById(`comment-${commentId}`);
                if (commentEl) {
                  commentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  commentEl.style.transition = 'background-color 0.5s';
                  commentEl.style.backgroundColor = 'rgba(168, 159, 207, 0.2)';
                  setTimeout(() => {
                    commentEl.style.backgroundColor = '';
                  }, 2000);
                }
              }, 100);
            }
          }, 2000);
        }, 100);
      }
    }
  }, [loading, location.hash]);

  const notifyMentions = async (text, postId, type = 'post', commentId = null) => {
    const mentions = text.match(/@([a-zA-Z0-9_À-ÿ.\-]+)/g);
    if (!mentions || !currentProfile) return;
    
    const names = [...new Set(mentions.map(m => m.slice(1)))];
    
    for (const name of names) {
      if (name.toLowerCase() === currentProfile.name.toLowerCase()) continue;
      const targetProfile = await db.getProfileByName(name);
      if (targetProfile) {
        await db.addNotification({
          targetProfileId: targetProfile.id,
          sourceProfileName: currentProfile.name,
          type: type === 'post' ? 'mention_post' : 'mention_comment',
          postId: postId,
          message: `${currentProfile.name} t'a mentionné dans un ${type === 'post' ? 'post' : 'commentaire'}.`,
          link: type === 'comment' && commentId ? `/forum#${postId}_${commentId}` : `/forum#${postId}`
        });
      }
    }
  };

  const handleMentionClick = async (e, mentionText) => {
    e.stopPropagation();
    const cleanName = mentionText.startsWith('@') ? mentionText.slice(1) : mentionText;
    const found = allProfiles?.find(p => p.name?.toLowerCase() === cleanName.toLowerCase());
    if (found) {
      navigate(`/profile/${found.id}`);
      return;
    }
    try {
      const p = await db.getProfileByName(cleanName);
      if (p) {
        navigate(`/profile/${p.id}`);
      } else {
        toast.info(`Profil @${cleanName} introuvable.`);
      }
    } catch (err) {
      toast.info(`Profil @${cleanName} introuvable.`);
    }
  };

  const renderTextWithMentions = (text) => {
    if (!text) return null;
    const parts = text.split(/(@[a-zA-Z0-9_À-ÿ.\-]+)/g);
    return parts.map((part, i) => {
      if (part.startsWith('@') && part.length > 1) {
        const username = part.slice(1);
        return (
          <span 
            key={i} 
            onClick={(e) => handleMentionClick(e, part)}
            style={{ 
              color: 'var(--primary-color)', 
              fontWeight: 'bold',
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationColor: 'rgba(168, 159, 207, 0.4)',
              transition: 'opacity 0.2s',
              display: 'inline-block'
            }}
            className="hover:opacity-80"
            title={`Voir le profil de ${username}`}
          >
            {part}
          </span>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setNewPostText(text);
    
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/@([a-zA-Z0-9_À-ÿ.\-]*)$/);
    
    if (match) {
      setMentionQuery(match[1]);
      const filtered = allProfiles.filter(p => p.name.toLowerCase().includes(match[1].toLowerCase()));
      setFilteredProfiles(filtered);
    } else {
      setMentionQuery(null);
    }
  };

  const insertMention = (profileName) => {
    if (!textareaRef.current) return;
    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = newPostText.slice(0, cursorPosition);
    const textAfterCursor = newPostText.slice(cursorPosition);
    
    const newTextBefore = textBeforeCursor.replace(/@[a-zA-Z0-9_À-ÿ.\-]*$/, `@${profileName} `);
    setNewPostText(newTextBefore + textAfterCursor);
    setMentionQuery(null);
    setTimeout(() => textareaRef.current.focus(), 0);
  };

  const insertEmoji = (emoji) => {
    if (!textareaRef.current) return;
    const cursorPosition = textareaRef.current.selectionStart;
    const textBeforeCursor = newPostText.slice(0, cursorPosition);
    const textAfterCursor = newPostText.slice(cursorPosition);
    
    setNewPostText(textBeforeCursor + emoji + textAfterCursor);
    setShowEmojiPicker(false);
    setTimeout(() => {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = cursorPosition + emoji.length;
      textareaRef.current.selectionEnd = cursorPosition + emoji.length;
    }, 0);
  };

  const handleCommentTextChange = (e) => {
    const text = e.target.value;
    setCommentText(text);
    
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = text.slice(0, cursorPosition);
    const match = textBeforeCursor.match(/@([a-zA-Z0-9_À-ÿ.\-]*)$/);
    
    if (match) {
      setCommentMentionQuery(match[1]);
      const filtered = allProfiles.filter(p => p.name.toLowerCase().includes(match[1].toLowerCase()));
      setFilteredCommentProfiles(filtered);
    } else {
      setCommentMentionQuery(null);
    }
  };

  const insertCommentMention = (profileName) => {
    if (!commentInputRef.current) return;
    const cursorPosition = commentInputRef.current.selectionStart;
    const textBeforeCursor = commentText.slice(0, cursorPosition);
    const textAfterCursor = commentText.slice(cursorPosition);
    
    const newTextBefore = textBeforeCursor.replace(/@[a-zA-Z0-9_À-ÿ.\-]*$/, `@${profileName} `);
    setCommentText(newTextBefore + textAfterCursor);
    setCommentMentionQuery(null);
    setTimeout(() => commentInputRef.current.focus(), 0);
  };

  const handlePublish = async () => {
    if (!newPostText.trim() && !selectedImage && !selectedRoutine) {
      toast.error("Écris un message, ajoute une photo ou sélectionne un programme à partager.");
      return;
    }
    if (!currentProfile) return;
    
    setIsPublishing(true);
    try {
      let imageUrl = null;
      if (typeof selectedImage === 'string') {
        imageUrl = selectedImage;
      } else if (selectedImage) {
        let uploadBlob = selectedImage;
        if (selectedImage.type !== 'image/gif') {
          uploadBlob = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const img = new Image();
              img.onload = () => {
                const canvas = document.createElement('canvas');
                const MAX_WIDTH = 600;
                const MAX_HEIGHT = 600;
                let width = img.width;
                let height = img.height;
                
                if (width > height) {
                  if (width > MAX_WIDTH) {
                    height = Math.round((height * MAX_WIDTH) / width);
                    width = MAX_WIDTH;
                  }
                } else {
                  if (height > MAX_HEIGHT) {
                    width = Math.round((width * MAX_HEIGHT) / height);
                    height = MAX_HEIGHT;
                  }
                }
                
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                canvas.toBlob(blob => resolve(blob), 'image/jpeg', 0.7);
              };
              img.src = e.target.result;
            };
            reader.readAsDataURL(selectedImage);
          });
        }
        imageUrl = await db.uploadPostMedia(uploadBlob);
      }
      
      const newPost = {
        profileId: currentProfile.id,
        profileName: currentProfile.name,
        profileImage: currentProfile.avatarUrl || null,
        text: newPostText.trim(),
        imageUrl,
        sharedRoutine: selectedRoutine ? selectedRoutine : null
      };
      
      const savedPost = await db.addPost(newPost);
      await notifyMentions(newPostText.trim(), savedPost.id, 'post');
      
      setNewPostText('');
      setSelectedImage(null);
      setImagePreview(null);
      setSelectedRoutine(null);
      if (location.state?.sharedRoutine) {
        window.history.replaceState({}, document.title);
      }
    } catch(e) {
      console.error("Error publishing post:", e);
      toast.error("Erreur lors de la publication.");
    } finally {
      setIsPublishing(false);
    }
  };

  const handleLike = async (post) => {
    if (!currentProfile) return;
    await db.toggleLikePost(post.id, currentProfile.id, post.likes);
  };

  const handleDelete = async (post) => {
    confirmDialog("Veux-tu vraiment supprimer ce post ?", async () => {
      await db.deletePost(post.id);
      toast.success("Post supprimé avec succès.");
    });
  };

  const handleAddComment = async (post) => {
    if (!commentText.trim() || !currentProfile) return;
    
    const comment = {
      profileId: currentProfile.id,
      profileName: currentProfile.name,
      profileImage: currentProfile.avatarUrl || null,
      text: commentText.trim(),
      parentId: replyingTo ? replyingTo.commentId : null,
      likes: []
    };
    
    const commentId = await db.addCommentToPost(post.id, post.comments, comment);
    
    if (replyingTo) {
      const targetComment = post.comments?.find(c => c.id === replyingTo.commentId);
      if (targetComment && targetComment.profileId && targetComment.profileId !== currentProfile.id) {
        await db.addNotification({
          targetProfileId: targetComment.profileId,
          message: `${currentProfile.name} a répondu à ton commentaire.`,
          link: `/forum#${post.id}_${commentId}`
        });
      }
    }

    await notifyMentions(commentText.trim(), post.id, 'comment', commentId);
    setCommentText('');
    setReplyingTo(null);
  };

  const handleToggleLikeComment = async (postId, comments, commentId) => {
    if (!currentProfile) return;
    await db.toggleLikeComment(postId, comments, commentId, currentProfile.id);
  };

  const timeAgo = (timestamp) => {
    const diff = Date.now() - timestamp;
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return "À l'instant";
    if (minutes < 60) return `Il y a ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Il y a ${hours} h`;
    const days = Math.floor(hours / 24);
    return `Il y a ${days} j`;
  };

  const renderLikesText = (post) => {
    const likes = post.likes || [];
    if (likes.length === 0) return null;
    if (!currentProfile) return `${likes.length} personne${likes.length > 1 ? 's ont' : ' a'} aimé ce post`;

    const myLike = likes.includes(currentProfile.id);
    const friendLikers = likes.filter(id => id !== currentProfile.id && currentProfile.friends?.includes(id));
    
    if (friendLikers.length === 0 && !myLike) {
      return `${likes.length} personne${likes.length > 1 ? 's ont' : ' a'} aimé ce post`;
    }

    if (likes.length === 1 && myLike) {
      return "Tu as aimé ce post";
    }

    const names = [];
    if (myLike) names.push("Toi");
    
    for (let i = 0; i < Math.min(friendLikers.length, 2); i++) {
      const friendProfile = allProfiles.find(p => p.id === friendLikers[i]);
      if (friendProfile) names.push(friendProfile.name);
    }

    const othersCount = likes.length - names.length;
    const verb = myLike ? "avez aimé" : "ont aimé";

    if (othersCount > 0) {
      return `${names.join(', ')} et ${othersCount} autre${othersCount > 1 ? 's' : ''} ${verb} ce post`;
    } else {
      const last = names.pop();
      return `${names.join(', ')} et ${last} ${verb} ce post`;
    }
  };

  return (
    <div style={{ maxWidth: '700px', margin: '0 auto', paddingBottom: '6rem' }}>
      <div style={{ marginBottom: '2.5rem' }}>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MessageSquare size={28} /> Forum
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
          Partage tes séances, tes progrès et échange avec la communauté !
        </p>
      </div>

      {/* PUBLISH BOX */}
      <div className="card" style={{ padding: '1.5rem', marginBottom: '2.5rem', position: 'relative', zIndex: (showEmojiPicker || showImageOptions || (mentionQuery !== null && filteredProfiles.length > 0)) ? 50 : 1, border: 'none', boxShadow: '0 8px 30px rgba(0,0,0,0.05)', borderRadius: '16px' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.5rem' }}>
          {currentProfile?.avatarUrl ? (
            <img loading="lazy" src={currentProfile.avatarUrl} alt="Profile" style={{ width: '45px', height: '45px', minWidth: '45px', flexShrink: 0, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--border-color)' }} />
          ) : (
            <div style={{ width: '45px', height: '45px', minWidth: '45px', flexShrink: 0, borderRadius: '50%', backgroundColor: 'var(--primary-color)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '1.2rem', border: '2px solid var(--border-color)' }}>
              {currentProfile?.name?.charAt(0) || '?'}
            </div>
          )}
          <div style={{ position: 'relative', flex: 1, paddingBottom: '15px' }}>
            <textarea 
              ref={textareaRef}
              placeholder="Quoi de neuf aujourd'hui ?" 
              style={{ width: '100%', minHeight: '60px', resize: 'none', backgroundColor: 'transparent', border: 'none', outline: 'none', fontSize: '1.1rem', padding: '0.5rem 40px 0.5rem 0', fontFamily: 'inherit', color: 'var(--text-primary)' }}
              value={newPostText}
              onChange={handleTextChange}
              maxLength={1000}
            />
            <div style={{ position: 'absolute', bottom: '0', right: '0', fontSize: '0.75rem', color: newPostText.length >= 1000 ? 'var(--danger-color)' : 'var(--text-secondary)' }}>
              {newPostText.length}/1000
            </div>
            
            <button 
              className="btn-icon" 
              style={{ position: 'absolute', top: '5px', right: '0px', color: 'var(--primary-color)', backgroundColor: 'rgba(108, 92, 231, 0.1)', padding: '6px', borderRadius: '50%', transition: 'all 0.2s' }}
              onClick={() => setShowEmojiPicker(!showEmojiPicker)}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(108, 92, 231, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(108, 92, 231, 0.1)'}
            >
              <Smile size={18} />
            </button>

            {showEmojiPicker && (
              <div style={{ position: 'absolute', top: '40px', right: '0', backgroundColor: 'var(--bg-card)', padding: '1rem', borderRadius: 'var(--border-radius)', boxShadow: 'var(--shadow)', border: '1px solid var(--border-color)', zIndex: 10, width: '280px', display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '0.5rem' }}>
                {EMOJIS.map(emoji => (
                  <button 
                    key={emoji}
                    style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', padding: '0.2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '4px' }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(168, 159, 207, 0.2)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            )}
            
            {mentionQuery !== null && filteredProfiles.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', zIndex: 10, boxShadow: 'var(--shadow)', maxHeight: '150px', overflowY: 'auto', marginTop: '4px' }}>
                {filteredProfiles.map(p => (
                  <div 
                    key={p.id} 
                    onClick={() => insertMention(p.name)}
                    style={{ padding: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}
                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(168, 159, 207, 0.1)'}
                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {p.avatarUrl ? (
                      <img loading="lazy" src={p.avatarUrl} alt={p.name} style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                    ) : (
                      <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: p.color || 'var(--primary-color)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>{p.name.charAt(0)}</div>
                    )}
                    <span style={{ fontWeight: 'bold' }}>{p.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        
        {imagePreview && (
          <div style={{ position: 'relative', marginBottom: '1rem', borderRadius: '8px', overflow: 'hidden' }}>
            <img loading="lazy" src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '250px', objectFit: 'contain', display: 'block', margin: '0 auto', borderRadius: '8px' }} />
            <button 
              className="btn-icon" 
              style={{ position: 'absolute', top: '5px', right: '5px', backgroundColor: 'rgba(0,0,0,0.5)', padding: '5px' }}
              onClick={() => { setImagePreview(null); setSelectedImage(null); }}
            >
              <X size={20} color="#fff" />
            </button>
          </div>
        )}

        {selectedRoutine && (
          <div style={{ position: 'relative', marginBottom: '1rem', padding: '1rem', borderRadius: '8px', border: '1px solid var(--secondary-color)', backgroundColor: 'var(--bg-color)', display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ backgroundColor: 'var(--secondary-color)', color: 'white', padding: '0.8rem', borderRadius: '50%' }}>
              <ClipboardList size={24} />
            </div>
            <div style={{ flex: 1 }}>
              <h4 style={{ margin: '0 0 0.3rem 0', color: 'var(--secondary-color)' }}>Programme partagé</h4>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                {selectedRoutine.name || 'Sans nom'}
              </div>
            </div>
            <button 
              className="btn-icon" 
              style={{ padding: '5px', color: 'var(--danger-color)' }}
              onClick={() => setSelectedRoutine(null)}
            >
              <X size={20} />
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)' }}>
          <div style={{ position: 'relative', display: 'flex', gap: '0.8rem', flexWrap: 'wrap' }}>
            <input 
              type="file" 
              accept="image/*" 
              ref={fileInputRef} 
              style={{ display: 'none' }} 
              onChange={handleImageChange}
            />
            <button 
              className="btn" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(108, 92, 231, 0.1)', color: 'var(--primary-color)', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none', fontWeight: 'bold', fontSize: '0.9rem', transition: 'all 0.2s', cursor: 'pointer' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(108, 92, 231, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(108, 92, 231, 0.1)'}
              onClick={() => setShowImageOptions(!showImageOptions)}
            >
              <ImageIcon size={18} /> <span className="hidden-mobile">Image</span>
            </button>
            <button 
              className="btn" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', color: 'var(--secondary-color)', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none', fontWeight: 'bold', fontSize: '0.9rem', transition: 'all 0.2s', cursor: 'pointer' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(34, 197, 94, 0.1)'}
              onClick={() => setShowGifSelector(true)}
            >
              <PlaySquare size={18} /> <span className="hidden-mobile">GIF</span>
            </button>
            <button 
              className="btn" 
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', backgroundColor: 'rgba(243, 156, 18, 0.1)', color: '#f39c12', padding: '0.5rem 1rem', borderRadius: '20px', border: 'none', fontWeight: 'bold', fontSize: '0.9rem', transition: 'all 0.2s', cursor: 'pointer' }}
              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(243, 156, 18, 0.2)'}
              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(243, 156, 18, 0.1)'}
              onClick={async () => {
                setShowRoutineSelector(true);
                const routines = await db.getRoutines(currentProfile.id);
                setUserRoutines(routines);
              }}
            >
              <ClipboardList size={18} /> <span className="hidden-mobile">Programme</span>
            </button>

            {showImageOptions && (
              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '0.5rem', backgroundColor: 'var(--bg-card)', padding: '1rem', borderRadius: 'var(--border-radius)', boxShadow: 'var(--shadow)', border: '1px solid var(--border-color)', zIndex: 10, width: '250px' }}>
                <button 
                  className="btn" 
                  style={{ width: '100%', marginBottom: '0.8rem', backgroundColor: 'var(--bg-color)', color: 'var(--text-primary)', border: '1px solid var(--border-color)' }}
                  onClick={() => { setShowImageOptions(false); fileInputRef.current?.click(); }}
                >
                  Depuis l'appareil
                </button>
                <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.5rem', fontWeight: 'bold' }}>OU URL (CONSEILLÉ POUR LES GIFS)</div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="https://..." 
                    value={imageUrlInput}
                    onChange={(e) => setImageUrlInput(e.target.value)}
                  />
                  <button 
                    className="btn btn-primary"
                    style={{ padding: '0 0.8rem' }}
                    onClick={() => {
                      if (imageUrlInput.trim()) {
                        setImagePreview(imageUrlInput.trim());
                        setSelectedImage(imageUrlInput.trim());
                        setShowImageOptions(false);
                        setImageUrlInput('');
                      }
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            )}
          </div>
          <button 
            className="btn btn-primary" 
            style={{ padding: '0.5rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', borderRadius: '20px', fontWeight: 'bold', boxShadow: '0 4px 15px rgba(108, 92, 231, 0.3)' }}
            onClick={handlePublish}
            disabled={isPublishing}
          >
            {isPublishing ? 'Publication...' : <>Publier <Send size={16} /></>}
          </button>
        </div>
      </div>

      {/* FEED */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Chargement du fil d'actualité...</div>
      ) : posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Aucun post pour le moment. Soyez le premier à publier !</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          {posts.map(post => {
            const hasLiked = currentProfile ? (post.likes || []).includes(currentProfile.id) : false;
            const commentsCount = (post.comments || []).length;
            const isAuthor = currentProfile && post.profileId === currentProfile.id;
            
            return (
              <div id={`post-${post.id}`} key={post.id} className="card" style={{ padding: '1.5rem', border: '1px solid var(--border-color)', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', borderRadius: '16px', backgroundColor: 'var(--bg-card)', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.2rem' }}>
                  <div 
                    style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', cursor: 'pointer' }}
                    onClick={() => navigate(`/profile/${post.profileId}`)}
                  >
                    <UserAvatar 
                      profile={allProfiles.find(p => p.id === post.profileId) || { id: post.profileId, name: post.profileName, avatarUrl: post.profileImage }} 
                      size={45} 
                      style={{ flexShrink: 0 }} 
                    />
                    <div>
                      <div 
                        style={{ fontWeight: 'bold', color: 'var(--primary-color)', fontSize: '1.05rem', transition: 'color 0.2s' }}
                        onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color-hover)'}
                        onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                      >
                        {post.profileName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{timeAgo(post.timestamp)}</div>
                    </div>
                  </div>
                  {isAuthor && (
                    <button className="btn-icon" onClick={() => handleDelete(post)}>
                      <Trash2 size={18} color="var(--danger-color)" />
                    </button>
                  )}
                </div>
                
                {post.text && <div style={{ marginBottom: '1rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderTextWithMentions(post.text)}</div>}
                
                {post.imageUrl && (
                  <div style={{ borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem', backgroundColor: 'rgba(0,0,0,0.03)', display: 'flex', justifyContent: 'center' }}>
                    <img loading="lazy" src={post.imageUrl} alt="Post content" style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '8px' }} />
                  </div>
                )}

                {post.sharedRoutine && (
                  <div style={{ borderRadius: '12px', border: '1px solid var(--secondary-color)', marginBottom: '1rem', backgroundColor: 'var(--bg-color)', overflow: 'hidden' }}>
                    <div 
                      onClick={() => setPreviewingRoutine(post.sharedRoutine)}
                      style={{ 
                        backgroundColor: 'var(--secondary-color)', 
                        padding: '0.75rem 1rem', 
                        color: 'white', 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '0.75rem', 
                        justifyContent: 'space-between',
                        cursor: 'pointer'
                      }}
                      title="Cliquer pour voir le détail des exercices"
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', minWidth: 0, flex: 1, overflow: 'hidden' }}>
                        <ClipboardList size={20} style={{ flexShrink: 0 }} /> 
                        <h4 style={{ margin: 0, fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Programme : {post.sharedRoutine.name || 'Sans nom'}
                        </h4>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {currentProfile && post.profileId !== currentProfile.id && (
                          <button 
                            className="btn" 
                            style={{ 
                              backgroundColor: 'white', 
                              color: 'var(--secondary-color)', 
                              padding: '0.35rem 0.75rem', 
                              fontSize: '0.78rem', 
                              border: 'none', 
                              borderRadius: '20px', 
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.3rem'
                            }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              const clonedRoutine = { ...post.sharedRoutine, profileId: currentProfile.id };
                              delete clonedRoutine.id;
                              await db.addRoutine(clonedRoutine);
                              toast.success("Programme ajouté à ta liste avec succès !");
                            }}
                          >
                            <Plus size={14} strokeWidth={2.5} />
                            <span>Importer</span>
                          </button>
                        )}
                      </div>
                    </div>
                    <div 
                      onClick={() => setPreviewingRoutine(post.sharedRoutine)}
                      style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', cursor: 'pointer' }}
                      title="Cliquer pour afficher la liste complète et les démonstrations"
                    >
                      {post.sharedRoutine.exercises && post.sharedRoutine.exercises.length > 0 ? (
                        <div style={{ fontSize: '0.9rem' }}>
                          <ul style={{ paddingLeft: '1.2rem', margin: '0', color: 'var(--text-secondary)' }}>
                            {post.sharedRoutine.exercises.slice(0, 5).map((ex, i) => (
                              <li key={i} style={{ marginBottom: '0.25rem' }}>
                                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                                  {ex.type === 'superset' ? 'Superset' : (ex.name || 'Exercice')}
                                </span>
                                {ex.sets && ex.sets.length > 0 ? ` (${ex.sets.length} séries)` : ''}
                              </li>
                            ))}
                          </ul>
                          {post.sharedRoutine.exercises.length > 5 && (
                            <span 
                              style={{ color: 'var(--primary-color)', fontSize: '0.85rem', marginTop: '0.5rem', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                            >
                              <Eye size={14} /> Voir les {post.sharedRoutine.exercises.length} exercices en détail...
                            </span>
                          )}
                        </div>
                      ) : (
                        <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Aucun exercice dans ce programme.</div>
                      )}
                    </div>
                  </div>
                )}
                
                <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '0.5rem' }}>
                  {renderLikesText(post) && (
                    <div style={{ fontSize: '0.85rem', fontStyle: 'italic', color: 'var(--text-secondary)', marginBottom: '0.8rem' }}>
                      {renderLikesText(post)}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '1.5rem' }}>
                    <button 
                      className="btn-icon" 
                      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: hasLiked ? '#e74c3c' : 'var(--text-secondary)', fontWeight: hasLiked ? 'bold' : 'normal', transition: 'transform 0.2s' }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onClick={() => handleLike(post)}
                    >
                      <Heart size={22} fill={hasLiked ? '#e74c3c' : 'none'} color={hasLiked ? '#e74c3c' : 'currentColor'} /> 
                    </button>
                    <button 
                      className="btn-icon" 
                      style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: activeCommentPost === post.id ? 'var(--primary-color)' : 'var(--text-secondary)', transition: 'transform 0.2s' }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                      onClick={() => setActiveCommentPost(activeCommentPost === post.id ? null : post.id)}
                    >
                      <MessageSquare size={22} /> 
                      <span style={{ fontSize: '0.9rem', fontWeight: '600' }}>{commentsCount > 0 ? commentsCount : ''}</span>
                    </button>
                  </div>
                </div>

                {/* COMMENTS SECTION */}
                {activeCommentPost === post.id && (
                  <div style={{ marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                    {post.comments && post.comments.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginBottom: '1rem' }}>
                        {(post.comments.filter(c => !c.parentId)).map(c => {
                          const replies = post.comments.filter(reply => reply.parentId === c.id);
                          const renderCommentObj = (comment, isReply = false) => {
                            const hasLiked = currentProfile && comment.likes && comment.likes.includes(currentProfile.id);
                            return (
                              <div id={`comment-${comment.id}`} key={comment.id} style={{ display: 'flex', gap: '0.5rem', marginLeft: isReply ? '2rem' : '0', marginTop: isReply ? '0.5rem' : '0', padding: '0.5rem', borderRadius: '8px' }}>
                                <UserAvatar 
                                  profile={allProfiles.find(p => p.id === comment.profileId) || { id: comment.profileId, name: comment.profileName, avatarUrl: comment.profileImage }} 
                                  size={isReply ? 24 : 28} 
                                  style={{ flexShrink: 0 }} 
                                />
                                <div style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.03)', padding: '0.8rem 1rem', borderRadius: '16px', borderTopLeftRadius: '4px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <span 
                                      style={{ fontWeight: 'bold', fontSize: '0.8rem', cursor: 'pointer', transition: 'color 0.2s' }}
                                      onClick={() => navigate(`/profile/${comment.profileId}`)}
                                      onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                                      onMouseOut={(e) => e.currentTarget.style.color = 'inherit'}
                                    >
                                      {comment.profileName}
                                    </span>
                                    <span style={{ fontSize: '0.65rem', color: 'var(--text-secondary)' }}>{timeAgo(comment.timestamp)}</span>
                                  </div>
                                  <div style={{ fontSize: '0.85rem', wordBreak: 'break-word', marginTop: '0.2rem' }}>{renderTextWithMentions(comment.text)}</div>
                                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.4rem', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                    <button style={{ background: 'none', border: 'none', color: hasLiked ? 'var(--primary-color)' : 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', padding: 0 }} onClick={() => handleToggleLikeComment(post.id, post.comments, comment.id)}>
                                      <Heart size={14} fill={hasLiked ? 'var(--primary-color)' : 'none'} /> {comment.likes?.length || 0}
                                    </button>
                                    {!isReply && (
                                      <button style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.3rem', padding: 0 }} onClick={() => { setReplyingTo({ commentId: comment.id, profileName: comment.profileName }); setTimeout(() => commentInputRef.current?.focus(), 50); }}>
                                        <CornerDownRight size={14} /> Répondre
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          };

                          return (
                            <React.Fragment key={c.id}>
                              {renderCommentObj(c, false)}
                              {replies.map(reply => renderCommentObj(reply, true))}
                            </React.Fragment>
                          );
                        })}
                      </div>
                    ) : (
                      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '1rem', textAlign: 'center' }}>Aucun commentaire. Soyez le premier !</div>
                    )}
                    
                    {replyingTo && (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: 'rgba(168, 159, 207, 0.1)', padding: '0.5rem 0.8rem', borderRadius: 'var(--border-radius-sm)', marginBottom: '0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                          <CornerDownRight size={14} /> En réponse à <strong>@{replyingTo.profileName}</strong>
                        </div>
                        <button className="btn-icon" style={{ padding: 0, width: '16px', height: '16px', minWidth: 'auto', minHeight: 'auto' }} onClick={() => setReplyingTo(null)}>
                          <X size={14} />
                        </button>
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.5rem', position: 'relative' }}>
                      <input 
                        ref={commentInputRef}
                        type="text" 
                        className="form-control" 
                        placeholder="Écrire un commentaire..." 
                        style={{ flex: 1, fontSize: '0.85rem' }}
                        value={commentText}
                        onChange={handleCommentTextChange}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleAddComment(post);
                        }}
                      />
                      
                      {commentMentionQuery !== null && filteredCommentProfiles.length > 0 && (
                        <div style={{ position: 'absolute', bottom: '100%', left: 0, width: '250px', backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius)', zIndex: 10, boxShadow: 'var(--shadow)', maxHeight: '150px', overflowY: 'auto', marginBottom: '4px' }}>
                          {filteredCommentProfiles.map(p => (
                            <div 
                              key={p.id} 
                              onClick={() => insertCommentMention(p.name)}
                              style={{ padding: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.8rem', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}
                              onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(168, 159, 207, 0.1)'}
                              onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                              {p.avatarUrl ? (
                                <img loading="lazy" src={p.avatarUrl} alt={p.name} style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' }} />
                              ) : (
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: p.color || 'var(--primary-color)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 'bold' }}>{p.name.charAt(0)}</div>
                              )}
                              <span style={{ fontWeight: 'bold' }}>{p.name}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <button 
                        className="btn btn-primary" 
                        style={{ padding: '0.4rem 0.8rem' }}
                        onClick={() => handleAddComment(post)}
                        disabled={!commentText.trim()}
                      >
                        <Send size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL DE SÉLECTION DE PROGRAMME */}
      {showRoutineSelector && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderBottom: '1px solid var(--border-color)' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><ClipboardList size={24} color="var(--secondary-color)" /> Partager un programme</h3>
              <button className="btn-icon" onClick={() => setShowRoutineSelector(false)}>
                <X size={24} />
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {userRoutines.length === 0 ? (
                <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>Aucun programme trouvé dans ta liste.</p>
              ) : (
                userRoutines.map(r => (
                  <div 
                    key={r.id} 
                    style={{ padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s', backgroundColor: 'var(--bg-color)' }}
                    onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--secondary-color)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                    onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-color)'; e.currentTarget.style.transform = 'none'; }}
                    onClick={() => {
                      setSelectedRoutine(r);
                      setShowRoutineSelector(false);
                    }}
                  >
                    <div style={{ fontWeight: 'bold', color: 'var(--secondary-color)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>
                      {r.name || 'Sans nom'}
                    </div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {r.exercises ? `${r.exercises.length} exercice(s)` : 'Aucun exercice'}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE SÉLECTION DE GIF */}
      {showGifSelector && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ width: '100%', maxWidth: '500px', height: '80vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><PlaySquare size={24} color="var(--primary-color)" /> Choisir un GIF</h3>
              <button className="btn-icon" onClick={() => setShowGifSelector(false)}>
                <X size={24} />
              </button>
            </div>
            
            <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)', backgroundColor: 'var(--bg-card)' }}>
              <input 
                type="text" 
                className="input" 
                placeholder="Rechercher un GIF..." 
                value={gifSearch}
                onChange={(e) => setGifSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchGifs(gifSearch)}
                style={{ width: '100%', padding: '0.8rem', borderRadius: '8px' }}
              />
              <button 
                className="btn btn-primary"
                onClick={() => fetchGifs(gifSearch)}
                style={{ width: '100%', marginTop: '0.5rem' }}
                disabled={isSearchingGifs}
              >
                {isSearchingGifs ? 'Recherche...' : 'Chercher'}
              </button>
            </div>

            <div 
              onScroll={handleGifScroll}
              style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', backgroundColor: 'var(--bg-color)', display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.5rem' }}
            >
              {isSearchingGifs && gifs.length === 0 ? (
                <div style={{ gridColumn: 'span 2', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Chargement des GIFs...</div>
              ) : !import.meta.env.VITE_GIPHY_API_KEY ? (
                <div style={{ gridColumn: 'span 2', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                  ⚠️ Clé API Giphy manquante. Contactez l'administrateur.
                </div>
              ) : gifs.length === 0 ? (
                <div style={{ gridColumn: 'span 2', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Aucun GIF trouvé.</div>
              ) : (
                <>
                  {gifs.map((gif, idx) => (
                    <div 
                      key={`${gif.id}-${idx}`}
                      onClick={() => {
                        setSelectedImage(gif.images.original.url);
                        setImagePreview(gif.images.original.url);
                        setShowGifSelector(false);
                        setGifSearch('');
                      }}
                      style={{ cursor: 'pointer', borderRadius: '8px', overflow: 'hidden', height: '150px', backgroundColor: 'var(--border-color)' }}
                    >
                      <img 
                        src={gif.images.fixed_height_small.url} 
                        alt={gif.title || "GIF"} 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        loading="lazy"
                      />
                    </div>
                  ))}
                  {isLoadingMoreGifs && (
                    <div style={{ gridColumn: 'span 2', textAlign: 'center', padding: '1rem', color: 'var(--text-secondary)' }}>
                      Chargement de plus de GIFs...
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE PRÉVISUALISATION D'UN PROGRAMME PARTAGÉ */}
      {previewingRoutine && (
        <div 
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '1rem', backdropFilter: 'blur(4px)'
          }}
          onClick={() => setPreviewingRoutine(null)}
        >
          <div 
            className="card modal-card" 
            style={{ 
              width: '100%', 
              maxWidth: '550px', 
              maxHeight: '85vh', 
              display: 'flex', 
              flexDirection: 'column', 
              padding: '1.5rem',
              transform: 'none'
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid var(--border-color)', paddingBottom: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <ClipboardList size={22} color="var(--primary-color)" />
                <h3 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text-primary)' }}>
                  {previewingRoutine.name || 'Programme'}
                </h3>
              </div>
              <button className="btn-icon" onClick={() => setPreviewingRoutine(null)}>
                <X size={20} />
              </button>
            </div>

            <p style={{ margin: '0 0 1rem 0', fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
              Clique sur un exercice pour afficher sa démonstration et ses détails.
            </p>

            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingRight: '0.25rem' }}>
              {previewingRoutine.exercises && previewingRoutine.exercises.length > 0 ? (
                previewingRoutine.exercises.map((block, idx) => {
                  if (block.type === 'superset' && block.items) {
                    return (
                      <div 
                        key={idx} 
                        style={{ 
                          padding: '0.8rem', 
                          backgroundColor: 'rgba(168, 159, 207, 0.08)', 
                          borderRadius: 'var(--border-radius-sm)', 
                          border: '1px dashed var(--primary-color)' 
                        }}
                      >
                        <div style={{ fontSize: '0.72rem', color: 'var(--primary-color)', fontWeight: 'bold', marginBottom: '0.5rem', letterSpacing: '1px' }}>
                          SUPERSET ({block.items.length} EXERCICES)
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {block.items.map((item, itemIdx) => {
                            const exDef = resolveExerciseDef(item);
                            return (
                              <div 
                                key={itemIdx}
                                onClick={() => setPreviewingExercise(exDef)}
                                style={{
                                  padding: '0.6rem 0.8rem',
                                  backgroundColor: 'var(--bg-card)',
                                  borderRadius: 'var(--border-radius-sm)',
                                  border: '1px solid var(--border-color)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                                className="hover:bg-border-color"
                              >
                                <div>
                                  <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{item.name}</div>
                                  <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                                    {item.sets ? `${item.sets.length} séries` : ''} {item.equipment ? `• ${item.equipment}` : ''}
                                  </div>
                                </div>
                                <span style={{ fontSize: '0.8rem', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                  <Eye size={15} /> Voir
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }

                  const exDef = resolveExerciseDef(block);
                  return (
                    <div 
                      key={idx}
                      onClick={() => setPreviewingExercise(exDef)}
                      style={{
                        padding: '0.8rem 1rem',
                        backgroundColor: 'var(--bg-card)',
                        borderRadius: 'var(--border-radius-sm)',
                        border: '1px solid var(--border-color)',
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                      className="hover:bg-border-color"
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{block.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {block.sets ? `${block.sets.length} séries` : ''} {block.equipment ? `• ${block.equipment}` : ''}
                        </div>
                      </div>
                      <span style={{ fontSize: '0.8rem', color: 'var(--primary-color)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        <Eye size={15} /> Voir
                      </span>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', padding: '2rem 0', color: 'var(--text-secondary)' }}>
                  Aucun exercice dans ce programme.
                </div>
              )}
            </div>

            {currentProfile && (
              <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border-color)', display: 'flex', gap: '0.8rem' }}>
                <button 
                  className="btn btn-primary" 
                  style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', padding: '0.8rem' }}
                  onClick={async () => {
                    const cloned = { ...previewingRoutine, profileId: currentProfile.id };
                    delete cloned.id;
                    await db.addRoutine(cloned);
                    toast.success("Programme ajouté à ta liste avec succès !");
                    setPreviewingRoutine(null);
                  }}
                >
                  <ClipboardList size={18} /> Ajouter à mes programmes
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal Démonstration Exercice au clic */}
      {previewingExercise && (
        <ExercisePreviewModal 
          exercise={previewingExercise}
          zIndex={10010}
          onClose={() => setPreviewingExercise(null)}
        />
      )}
    </div>
  );
};

export default Forum;
